import React, { useState, useEffect } from 'react';
import { Row, Col, Badge, Card, CardBody, CardHeader } from 'reactstrap';
import { ListOrdered, GripVertical, Minus, Split, ChefHat, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMenuStore } from '../../../stores/Assets/menu';
import { useSiteStore } from '../../../stores/Assets/site';

const THEME_COLOR = '#c59253';

const MenuLinking = ({ state, updateState, onNext }) => {
    const { sites } = useSiteStore();
    const { menus, getMenus } = useMenuStore();
    const [unassignedMenus, setUnassignedMenus] = useState([]);
    const [dragOverStationId, setDragOverStationId] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);

    // Check if there are already any links to determine initial state
    const hasExistingLinks = Object.values(state.menuLinks).some(links => links.length > 0);
    const [isConfiguring, setIsConfiguring] = useState(hasExistingLinks);

    const siteId = sites?.[0]?.site_id;

    useEffect(() => {
        if (siteId) {
            getMenus(siteId);
        }
    }, [siteId]);

    useEffect(() => {
        if (menus) {
            // Initial unassigned menus are those not in any station's menuLinks
            const linkedMenuIds = Object.values(state.menuLinks).flat();
            setUnassignedMenus(menus.filter(m => !linkedMenuIds.includes(m.menu_id)));
        }
    }, [menus, state.menuLinks]);

    const toggleSelection = (id) => {
        setSelectedItems(prev => {
            if (prev.includes(id)) return prev.filter(i => i !== id);
            return [...prev, id];
        });
    };

    const handleDragStart = (e, ids) => {
        e.dataTransfer.setData('menuIds', JSON.stringify(ids));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDrop = (e, stationId) => {
        e.preventDefault();
        setDragOverStationId(null);
        const data = e.dataTransfer.getData('menuIds');
        if (!data) return;

        const droppedIds = JSON.parse(data);
        const currentLinks = { ...state.menuLinks };

        // Add to new station
        currentLinks[stationId] = [...new Set([...(currentLinks[stationId] || []), ...droppedIds])];

        // Remove from other stations if they were there
        Object.keys(currentLinks).forEach(sid => {
            if (sid != stationId) {
                currentLinks[sid] = currentLinks[sid].filter(id => !droppedIds.includes(id));
            }
        });

        updateState({ menuLinks: currentLinks });
        setSelectedItems([]);
    };

    const unassignMenu = (stationId, menuId) => {
        const currentLinks = { ...state.menuLinks };
        currentLinks[stationId] = currentLinks[stationId].filter(id => id !== menuId);
        updateState({ menuLinks: currentLinks });
    };

    if (!isConfiguring) {
        return (
            <div className="container py-5 h-100 d-flex flex-column align-items-center justify-content-center">
                <div className="text-center mb-5">
                    <div className="badge rounded-pill px-4 py-2 border mb-3 text-uppercase fw-bold" style={{ color: THEME_COLOR, backgroundColor: 'rgba(197, 146, 83, 0.1)', letterSpacing: '1px', fontSize: '10px' }}>
                        Step 3: Routing Logic
                    </div>
                    <h2 className="display-6 fw-bold mb-3">Does your site have specialty stations?</h2>
                    <p className="text-muted mx-auto lead" style={{ maxWidth: '600px' }}>
                        Decide how orders should reach your kitchens. You can always change this later.
                    </p>
                </div>

                <div className="row g-4 w-100" style={{ maxWidth: '900px' }}>
                    <div className="col-md-6">
                        <div
                            onClick={onNext}
                            className="card h-100 border-2 rounded-4 p-4 cursor-pointer transition-all hover-shadow-lg border-hover-light"
                            style={{ transition: 'all 0.3s ease' }}
                        >
                            <div className="card-body p-0 d-flex flex-column">
                                <div className="mb-4 d-flex align-items-center justify-content-center bg-light rounded-4" style={{ width: '64px', height: '64px' }}>
                                    <Info size={32} className="text-muted" />
                                </div>
                                <h4 className="fw-bold mb-2">Default: Space-Based</h4>
                                <p className="text-muted small flex-grow-1">
                                    Orders automatically go to the station assigned to where the customer is sitting.
                                </p>
                                <div className="mt-4 pt-3 border-top">
                                    <label className="text-uppercase fw-bold text-muted" style={{ fontSize: '10px' }}>Best for</label>
                                    <div className="d-flex gap-2 mt-2">
                                        <Badge color="soft-secondary" className="px-3 py-2 rounded-pill">Restaurants</Badge>
                                        <Badge color="soft-secondary" className="px-3 py-2 rounded-pill">Food Courts</Badge>
                                    </div>
                                </div>
                                <button className="btn btn-outline-secondary rounded-pill w-100 mt-4 fw-bold py-2">Use Standard Routing</button>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div
                            onClick={() => setIsConfiguring(true)}
                            className="card h-100 border-2 rounded-4 p-4 cursor-pointer transition-all border-primary-fade shadow-sm"
                            style={{
                                transition: 'all 0.3s ease',
                                border: `2px solid ${THEME_COLOR}`
                            }}
                        >
                            <div className="card-body p-0 d-flex flex-column">
                                <div className="mb-4 d-flex align-items-center justify-content-center rounded-4" style={{ width: '64px', height: '64px', backgroundColor: 'rgba(197, 146, 83, 0.1)' }}>
                                    <Split size={32} style={{ color: THEME_COLOR }} />
                                </div>
                                <h4 className="fw-bold mb-2">Specialty: Menu-Based</h4>
                                <p className="text-muted small flex-grow-1">
                                    Force specific menus (like <strong>Bar</strong> or <strong>Coffee</strong>) to bypass spaces and go to a specific station.
                                </p>
                                <div className="mt-4 pt-3 border-top">
                                    <label className="text-uppercase fw-bold text-muted" style={{ fontSize: '10px' }}>Best for</label>
                                    <div className="d-flex gap-2 mt-2">
                                        <Badge color="soft-primary" className="px-3 py-2 rounded-pill">Cocktail Bars</Badge>
                                        <Badge color="soft-primary" className="px-3 py-2 rounded-pill">Baristas</Badge>
                                    </div>
                                </div>
                                <button className="btn text-white rounded-pill w-100 mt-4 fw-bold py-2 shadow-sm" style={{ backgroundColor: THEME_COLOR }}>Configure Specialty</button>
                            </div>
                        </div>
                    </div>
                </div>

                <style>{`
                    .cursor-pointer { cursor: pointer; }
                    .hover-shadow-lg:hover { transform: translateY(-5px); box-shadow: 0 1rem 3rem rgba(0,0,0,.1) !important; }
                    .border-hover-light:hover { border-color: #dee2e6 !important; }
                    .border-primary-fade { transform: translateY(-5px); box-shadow: 0 1rem 3rem rgba(197, 146, 83, 0.1) !important; }
                    .bg-soft-primary { background-color: rgba(197, 146, 83, 0.1) !important; }
                    .text-primary { color: #c59253 !important; }
                    .badge-soft-secondary { background-color: #f3f4f6; color: #6b7280; }
                    .badge-soft-primary { background-color: rgba(197, 146, 83, 0.1); color: #c59253; }
                `}</style>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4" style={{ height: 'calc(100vh - 180px)' }}>
            <div className="mb-4 px-3 d-flex justify-content-between align-items-center">
                <div>
                    <h4 className="fw-bold mb-1">Route Menus to Stations</h4>
                    <p className="text-muted mb-0">Drag menus to assign them to specific kitchen stations. These associations have the highest priority.</p>
                </div>
                <button
                    onClick={() => setIsConfiguring(false)}
                    className="btn btn-link text-muted text-decoration-none small"
                >
                    Back to explanation
                </button>
            </div>
            <Row className="h-100 g-4">
                {/* Sidebar: Unassigned Menus */}
                <Col lg={3} className="h-100">
                    <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden">
                        <CardHeader className="bg-white p-4 border-bottom">
                            <div className="d-flex align-items-center justify-content-between">
                                <h5 className="mb-0 fw-bold">Global Menus</h5>
                                <Badge color="soft-primary">{unassignedMenus.length}</Badge>
                            </div>
                        </CardHeader>
                        <CardBody className="p-3 overflow-auto">
                            <AnimatePresence mode="popLayout">
                                {unassignedMenus.map(menu => {
                                    const isSelected = selectedItems.includes(menu.menu_id);
                                    return (
                                        <motion.div
                                            key={menu.menu_id}
                                            layout
                                            draggable
                                            onDragStart={(e) => {
                                                const ids = isSelected ? selectedItems : [menu.menu_id];
                                                if (!isSelected) toggleSelection(menu.menu_id);
                                                handleDragStart(e, ids);
                                            }}
                                            onClick={() => toggleSelection(menu.menu_id)}
                                            className={`p-3 rounded-3 border mb-2 d-flex align-items-center gap-2 cursor-grab transition-all ${isSelected ? 'border-primary bg-soft-primary' : 'bg-white hover-light'}`}
                                        >
                                            <GripVertical size={16} className="text-muted" />
                                            <span className="small fw-bold text-truncate">{menu.name}</span>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                            {unassignedMenus.length === 0 && (
                                <div className="text-center py-5 opacity-50 border border-dashed rounded-3">
                                    <ListOrdered size={32} className="mb-2" />
                                    <p className="small mb-0">All menus assigned</p>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                {/* Main: Stations Grid */}
                <Col lg={9} className="h-100 overflow-auto pe-3">
                    <Row className="g-4">
                        {state.stations.map((station) => (
                            <Col key={station.id} md={6} xl={4}>
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setDragOverStationId(station.id); }}
                                    onDragLeave={() => setDragOverStationId(null)}
                                    onDrop={(e) => handleDrop(e, station.id)}
                                    className={`bg-white rounded-4 shadow-sm border h-100 overflow-hidden d-flex flex-column transition-all ${dragOverStationId === station.id ? 'border-primary ring-2 ring-primary bg-soft-primary' : ''}`}
                                    style={{
                                        minHeight: '200px',
                                        transform: dragOverStationId === station.id ? 'scale(1.02)' : 'none'
                                    }}
                                >
                                    <div className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-1 bg-white rounded-2 border">
                                                <ChefHat size={16} style={{ color: THEME_COLOR }} />
                                            </div>
                                            <h6 className="fw-bold mb-0">{station.name}</h6>
                                        </div>
                                        <Badge color="soft-primary">
                                            {(state.menuLinks[station.id] || []).length} Menus
                                        </Badge>
                                    </div>

                                    <div className="p-3 flex-grow-1">
                                        <div className="d-flex flex-column gap-2">
                                            {(state.menuLinks[station.id] || []).map(menuId => {
                                                const menu = menus.find(m => m.menu_id === menuId);
                                                return (
                                                    <div key={menuId} className="bg-white border rounded-3 p-2 px-3 d-flex justify-content-between align-items-center shadow-sm">
                                                        <span className="small fw-bold text-truncate">{menu?.name}</span>
                                                        <button
                                                            onClick={() => unassignMenu(station.id, menuId)}
                                                            className="btn btn-link p-0 text-danger border-0 hov-scale"
                                                        >
                                                            <Minus size={14} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                            {(!state.menuLinks[station.id] || state.menuLinks[station.id].length === 0) && (
                                                <div className="text-center py-4 border-2 border-dashed rounded-3 opacity-25 d-flex flex-column align-items-center gap-2">
                                                    <Split size={24} />
                                                    <span style={{ fontSize: '10px' }}>Drop Menus Here</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>

            <style>{`
                .bg-soft-primary { background-color: rgba(197, 146, 83, 0.1) !important; }
                .hover-light:hover { background-color: #f8f9fa !important; }
                .cursor-grab { cursor: grab; }
                .cursor-grab:active { cursor: grabbing; }
                .hov-scale:hover { transform: scale(1.2); }
                .ring-2 { box-shadow: 0 0 0 2px #c59253; }
                .text-primary { color: #c59253 !important; }
            `}</style>
        </div>
    );
};

export default MenuLinking;
